import React, { useEffect, useState, useCallback } from "react";
import {
  Card,
  CardHeader,
  CardBody,
  Container,
  Row,
  Col,
  Table,
  Button,
  Input,
  Pagination,
  PaginationItem,
  PaginationLink,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "reactstrap";
import {
  postMethod,
  getUserName,
  getMethod,
  getBranchCode,
} from "services/httpServices";
import { useToasts } from "react-toast-notifications";
import moment from "moment";
import { getAmt } from "services/util";
import FullPageLoader from "components/FullPageLoader/fullpageloader";
import NoRecordFound from "components/NoRecordFound/NoRecordFound";

const PendingDisbursement = () => {
  const [disbursements, setDisbursements] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [disbursing, setDisbursing] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [selectedLoansForDisbursement, setSelectedLoansForDisbursement] =
    useState([]);
  const [sortConfig, setSortConfig] = useState({
    key: "loanApprovalDate",
    direction: "desc",
  });
  const [branches, setBranches] = useState([]);
  const [branchFilter, setBranchFilter] = useState("");
  const [allDisbursements, setAllDisbursements] = useState([]);
  const { addToast } = useToasts();

  const fetchBranches = async () => {
    try {
      const response = await getMethod("/api/BankProfile/GetBranches", "get");
      if (response && response.data) {
        // Check if response is an array directly or wrapped in result
        let branchData = [];
        if (Array.isArray(response.data)) {
          branchData = response.data;
        } else if (
          response.data.responseCode === "200" &&
          response.data.result
        ) {
          branchData = Array.isArray(response.data.result)
            ? response.data.result
            : [];
        }
        setBranches(branchData);
      }
    } catch (error) {
      console.error("Error fetching branches:", error);
    }
  };

  const fetchDisbursements = useCallback(async () => {
    setLoading(true);
    try {
      const response = await postMethod("/api/Subscriber/GetApprovedLoan", {});
      if (response && response.data) {
        if (response.data.responseCode === "200" && response.data.result) {
          const loanData = response.data.result.data;
          // Filter by product type and map to loan records
          const loanRecords = loanData
            .filter(
              (row) =>
                row &&
                row[3] &&
                (row[3].includes("Digital Regular Loan") ||
                  row[3].includes("Digital Small Business Loan") ||
                  row[3].includes("Digital Consumer Loan") ||
                  row[3].includes("Digital Special Loan - Monthly") ||
                  row[3].includes("Digital Group Special Loan"))
            )
            .map((record, index) => ({
              id: index + 1,
              creditOfficer: record[0],
              clientId: record[1],
              account: record[2],
              productName: record[3],
              branchId: record[4],
              branchName: record[5],
              branchCode: String(record[4] || "").trim(), // Ensure it's a string and trimmed
              loanApprovalDate: record[6],
              firstName: record[7],
              lastName: record[8],
              middleName: record[9],
              mobileNumber: record[10],
              scheduleStartDate: record[11],
              loanAmount: parseFloat(record[12]) || 0,
              selected: false,
            }));

          setAllDisbursements(loanRecords);
        } else {
          addToast(
            response.data.responseMessage || "Error fetching approved loans",
            {
              appearance: "error",
            }
          );
        }
      } else {
        addToast("Error fetching approved loans", {
          appearance: "error",
        });
      }
    } catch (error) {
      addToast("Error fetching approved loans", {
        appearance: "error",
      });
    } finally {
      setLoading(false);
    }
  }, [addToast]);

  useEffect(() => {
    fetchBranches();
    // Set default branch filter from user's BranchCode if available
    const userBranchCode = getBranchCode();
    if (userBranchCode) {
      setBranchFilter(userBranchCode);
    }
  }, []);

  useEffect(() => {
    fetchDisbursements();
  }, [fetchDisbursements]);

  useEffect(() => {
    // Apply frontend filter when branch filter changes or when data is loaded
    if (allDisbursements.length > 0) {
      let filtered = allDisbursements;
      if (branchFilter) {
        const filterValue = String(branchFilter).trim();
        filtered = allDisbursements.filter(
          (loan) => String(loan.branchCode || "").trim() === filterValue
        );
      }
      setDisbursements(filtered);
      setTotalPages(Math.ceil(filtered.length / 10));
      setCurrentPage(0); // Reset to first page when filtering
    }
  }, [branchFilter, allDisbursements]);

  const handleSelectLoan = (id) => {
    setDisbursements((prev) =>
      prev.map((loan) =>
        loan.id === id ? { ...loan, selected: !loan.selected } : loan
      )
    );
  };

  const handleSelectAll = () => {
    const allSelected = disbursements.every((loan) => loan.selected);
    setDisbursements((prev) =>
      prev.map((loan) => ({ ...loan, selected: !allSelected }))
    );
  };

  const handleDisburseSelected = () => {
    const selectedLoans = disbursements.filter((loan) => loan.selected);
    if (selectedLoans.length === 0) {
      addToast("Please select at least one loan to disburse", {
        appearance: "warning",
      });
      return;
    }

    setSelectedLoansForDisbursement(selectedLoans);
    setShowConfirmModal(true);
  };

  const confirmDisbursement = async () => {
    setDisbursing(true);
    setShowConfirmModal(false);

    try {
      // Format phone number to include + if not present
      const formatPhoneNumber = (phone) => {
        if (!phone) return "";
        return phone.startsWith("+") ? phone : `+${phone}`;
      };

      // Format full name to uppercase
      const formatFullName = (firstName, middleName, lastName) => {
        const parts = [firstName, middleName, lastName].filter(Boolean);
        return parts.join(" ").toUpperCase();
      };

      // Create payload in the required format
      const payload = selectedLoansForDisbursement.map((loan) => ({
        account: loan.account,
        phone: formatPhoneNumber(loan.mobileNumber),
        amount: loan.loanAmount,
        name: formatFullName(loan.firstName, loan.middleName, loan.lastName),
        product: loan.productName,
        scheduleStartDate: loan.scheduleStartDate,
        logBy: getUserName() || "USERNAME",
      }));

      // Call the disbursement API
      const response = await postMethod(
        "/api/Transactions/DisburseLoan",
        payload,
        "post"
      );
      if (response && response.data) {
        if (response.data.responseCode === "200") {
          addToast(
            response.data.responseMessage ||
              `Successfully disbursed ${selectedLoansForDisbursement.length} loan(s)`,
            {
              appearance: "success",
            }
          );

          // Reset selections
          setDisbursements((prev) =>
            prev.map((loan) => ({ ...loan, selected: false }))
          );

          // Refresh the disbursements list
          await fetchDisbursements();
        } else {
          addToast(response.data.responseMessage || "Error disbursing loans", {
            appearance: "error",
          });
        }
      } else {
        addToast("Error disbursing loans", {
          appearance: "error",
        });
      }
    } catch (error) {
      console.error("Error disbursing loans:", error);
      addToast("Error disbursing loans", {
        appearance: "error",
      });
    } finally {
      setDisbursing(false);
      setSelectedLoansForDisbursement([]);
    }
  };

  const cancelDisbursement = () => {
    setShowConfirmModal(false);
    setSelectedLoansForDisbursement([]);
  };

  const handleSort = (key) => {
    setSortConfig((prevConfig) => ({
      key,
      direction:
        prevConfig.key === key && prevConfig.direction === "asc"
          ? "desc"
          : "asc",
    }));
  };

  const getSortIcon = (columnKey) => {
    if (sortConfig.key !== columnKey) {
      return <i className="fas fa-sort text-muted" />;
    }
    return sortConfig.direction === "asc" ? (
      <i className="fas fa-sort-up text-primary" />
    ) : (
      <i className="fas fa-sort-down text-primary" />
    );
  };

  const sortData = (data) => {
    return [...data].sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];

      // Handle different data types
      if (
        sortConfig.key === "loanApprovalDate" ||
        sortConfig.key === "scheduleStartDate"
      ) {
        aValue = new Date(aValue);
        bValue = new Date(bValue);
      } else if (sortConfig.key === "loanAmount") {
        aValue = parseFloat(aValue) || 0;
        bValue = parseFloat(bValue) || 0;
      } else {
        // String comparison
        aValue = String(aValue || "").toLowerCase();
        bValue = String(bValue || "").toLowerCase();
      }

      if (aValue < bValue) {
        return sortConfig.direction === "asc" ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === "asc" ? 1 : -1;
      }
      return 0;
    });
  };

  return (
    <>
      <div className="header bg-gradient-info pb-8 pt-5 pt-md-8">
        <Container fluid>
          <div className="header-body">
            <Row>
              <Col lg="4" xl="4">
                <Card className="card-stats mb-4 mb-xl-0">
                  <CardBody>
                    <Row>
                      <div className="col">
                        <h5 className="card-title text-uppercase text-muted mb-0">
                          Total Loans
                        </h5>
                        <span className="h2 font-weight-bold mb-0">
                          {disbursements.length}
                        </span>
                      </div>
                      <Col className="col-auto">
                        <div className="icon icon-shape bg-info text-white rounded-circle shadow">
                          <i className="fas fa-file-invoice" />
                        </div>
                      </Col>
                    </Row>
                  </CardBody>
                </Card>
              </Col>
              <Col lg="4" xl="4">
                <Card className="card-stats mb-4 mb-xl-0">
                  <CardBody>
                    <Row>
                      <div className="col">
                        <h5 className="card-title text-uppercase text-muted mb-0">
                          Total Amount
                        </h5>
                        <span className="h2 font-weight-bold mb-0">
                          {getAmt(
                            disbursements.reduce(
                              (sum, loan) => sum + loan.loanAmount,
                              0
                            )
                          )}
                        </span>
                      </div>
                      <Col className="col-auto">
                        <div className="icon icon-shape bg-success text-white rounded-circle shadow">
                          <i className="fas fa-money-bill" />
                        </div>
                      </Col>
                    </Row>
                  </CardBody>
                </Card>
              </Col>
              <Col lg="4" xl="4">
                <Card className="card-stats mb-4 mb-xl-0">
                  <CardBody>
                    <Row>
                      <div className="col">
                        <h5 className="card-title text-uppercase text-muted mb-0">
                          Selected
                        </h5>
                        <span className="h2 font-weight-bold mb-0">
                          {disbursements.filter((loan) => loan.selected).length}
                        </span>
                      </div>
                      <Col className="col-auto">
                        <div className="icon icon-shape bg-warning text-white rounded-circle shadow">
                          <i className="fas fa-check-circle" />
                        </div>
                      </Col>
                    </Row>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          </div>
        </Container>
      </div>

      <Container className="mt--7" fluid>
        <Row>
          <div className="col">
            <Card className="shadow">
              <CardHeader className="bg-transparent">
                <div className="d-flex justify-content-between align-items-center">
                  <h3 className="mb-0">Approved Loans for Disbursement</h3>
                  <div style={{ minWidth: "200px" }}>
                    <Input
                      type="select"
                      value={branchFilter}
                      onChange={(e) => setBranchFilter(e.target.value)}
                      disabled={!!getBranchCode()}
                    >
                      <option value="">All Branches</option>
                      {branches.map((branch) => (
                        <option key={branch.id} value={branch.branchCode}>
                          {branch.branchName} ({branch.branchCode})
                        </option>
                      ))}
                    </Input>
                  </div>
                </div>
              </CardHeader>
              <CardBody>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <div>
                    <Button
                      color="primary"
                      size="sm"
                      onClick={handleSelectAll}
                      className="mr-2"
                    >
                      {disbursements.every((loan) => loan.selected)
                        ? "Deselect All"
                        : "Select All"}
                    </Button>
                    <Button
                      color="success"
                      size="sm"
                      onClick={handleDisburseSelected}
                      disabled={
                        disbursing ||
                        disbursements.filter((loan) => loan.selected).length ===
                          0
                      }
                    >
                      {disbursing
                        ? "Disbursing..."
                        : `Disburse Selected (${
                            disbursements.filter((loan) => loan.selected).length
                          })`}
                    </Button>
                  </div>
                </div>

                <div className="table-responsive">
                  <Table className="align-items-center table-flush" responsive>
                    <thead className="thead-light">
                      <tr>
                        <th scope="col" className="text-center">
                          <Input
                            type="checkbox"
                            checked={
                              disbursements.length > 0 &&
                              disbursements.every((loan) => loan.selected)
                            }
                            onChange={handleSelectAll}
                            style={{ margin: 0 }}
                          />
                        </th>
                        <th
                          scope="col"
                          style={{ cursor: "pointer" }}
                          onClick={() => handleSort("clientId")}
                        >
                          Client ID {getSortIcon("clientId")}
                        </th>
                        <th
                          scope="col"
                          style={{ cursor: "pointer" }}
                          onClick={() => handleSort("account")}
                        >
                          Account {getSortIcon("account")}
                        </th>
                        <th
                          scope="col"
                          style={{ cursor: "pointer" }}
                          onClick={() => handleSort("firstName")}
                        >
                          Name {getSortIcon("firstName")}
                        </th>
                        <th
                          scope="col"
                          style={{ cursor: "pointer" }}
                          onClick={() => handleSort("mobileNumber")}
                        >
                          Mobile {getSortIcon("mobileNumber")}
                        </th>
                        <th
                          scope="col"
                          style={{ cursor: "pointer" }}
                          onClick={() => handleSort("productName")}
                        >
                          Product {getSortIcon("productName")}
                        </th>
                        <th
                          scope="col"
                          style={{ cursor: "pointer" }}
                          onClick={() => handleSort("branchName")}
                        >
                          Branch {getSortIcon("branchName")}
                        </th>
                        <th
                          scope="col"
                          style={{ cursor: "pointer" }}
                          onClick={() => handleSort("loanAmount")}
                        >
                          Amount {getSortIcon("loanAmount")}
                        </th>
                        <th
                          scope="col"
                          style={{ cursor: "pointer" }}
                          onClick={() => handleSort("loanApprovalDate")}
                        >
                          Approval Date {getSortIcon("loanApprovalDate")}
                        </th>
                        <th
                          scope="col"
                          style={{ cursor: "pointer" }}
                          onClick={() => handleSort("creditOfficer")}
                        >
                          Credit Officer {getSortIcon("creditOfficer")}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortData(disbursements)
                        .slice(currentPage * 10, (currentPage + 1) * 10)
                        .map((loan, index) => (
                          <tr key={loan.id}>
                            <td className="text-center align-middle">
                              <Input
                                type="checkbox"
                                checked={loan.selected}
                                onChange={() => handleSelectLoan(loan.id)}
                                style={{ margin: 0 }}
                              />
                            </td>
                            <td className="align-middle">{loan.clientId}</td>
                            <td className="align-middle">{loan.account}</td>
                            <td className="align-middle">
                              {loan.firstName} {loan.middleName || ""}{" "}
                              {loan.lastName}
                            </td>
                            <td className="align-middle">
                              {loan.mobileNumber}
                            </td>
                            <td className="align-middle">{loan.productName}</td>
                            <td className="align-middle">{loan.branchName}</td>
                            <td className="align-middle">
                              {getAmt(loan.loanAmount)}
                            </td>
                            <td className="align-middle">
                              {moment(loan.loanApprovalDate).format(
                                "DD/MM/YYYY"
                              )}
                            </td>
                            <td className="align-middle">
                              {loan.creditOfficer}
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </Table>
                </div>

                {loading && <FullPageLoader label="Loading..." />}

                {!loading && disbursements.length === 0 && <NoRecordFound />}

                {totalPages > 1 && (
                  <div className="d-flex justify-content-center mt-3">
                    <Pagination>
                      <PaginationItem disabled={currentPage === 0}>
                        <PaginationLink
                          previous
                          onClick={() => setCurrentPage(currentPage - 1)}
                        />
                      </PaginationItem>
                      {[...Array(totalPages)].map((_, index) => (
                        <PaginationItem
                          key={index}
                          active={index === currentPage}
                        >
                          <PaginationLink onClick={() => setCurrentPage(index)}>
                            {index + 1}
                          </PaginationLink>
                        </PaginationItem>
                      ))}
                      <PaginationItem disabled={currentPage === totalPages - 1}>
                        <PaginationLink
                          next
                          onClick={() => setCurrentPage(currentPage + 1)}
                        />
                      </PaginationItem>
                    </Pagination>
                  </div>
                )}
              </CardBody>
            </Card>
          </div>
        </Row>
      </Container>

      {/* Confirmation Modal */}
      <Modal isOpen={showConfirmModal} toggle={cancelDisbursement} size="lg">
        <ModalHeader toggle={cancelDisbursement}>
          Confirm Disbursement
        </ModalHeader>
        <ModalBody>
          <div className="mb-3">
            <h5>Are you sure you want to disburse the following loans?</h5>
            <p className="text-muted">
              This action will process {selectedLoansForDisbursement.length}{" "}
              loan(s) for disbursement.
            </p>
          </div>

          <div className="mb-3">
            <h6>Summary:</h6>
            <ul className="list-unstyled">
              <li>
                <strong>Total Loans:</strong>{" "}
                {selectedLoansForDisbursement.length}
              </li>
              <li>
                <strong>Total Amount:</strong>{" "}
                {getAmt(
                  selectedLoansForDisbursement.reduce(
                    (sum, loan) => sum + loan.loanAmount,
                    0
                  )
                )}
              </li>
            </ul>
          </div>

          <div className="table-responsive">
            <Table size="sm" className="mb-0">
              <thead>
                <tr>
                  <th>Client ID</th>
                  <th>Name</th>
                  <th>Account</th>
                  <th>Amount</th>
                </tr>
              </thead>
              <tbody>
                {selectedLoansForDisbursement.map((loan, index) => (
                  <tr key={index}>
                    <td>{loan.clientId}</td>
                    <td>
                      {loan.firstName} {loan.middleName || ""} {loan.lastName}
                    </td>
                    <td>{loan.account}</td>
                    <td>{getAmt(loan.loanAmount)}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={cancelDisbursement}>
            Cancel
          </Button>
          <Button
            color="success"
            onClick={confirmDisbursement}
            disabled={disbursing}
          >
            {disbursing ? "Processing..." : "Confirm Disbursement"}
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default PendingDisbursement;
